import React from 'react'
import { Box, Typography, LinearProgress, Button, Divider } from '@mui/material'
import { WalletBalanceResponse } from '../../../../services/rewardsService'
import { SubscriptionTier } from '../../../../config/subscription-tiers'

interface MonthlyCreditHeroProps {
  /** Live wallet balance (monthly progress + lifetime redeemable). Null while loading / on error. */
  wallet: WalletBalanceResponse | null
  /** Resolved tier for goal + rollover fallback when the backend omits them. */
  tier: SubscriptionTier
  onRedeem: () => void
}

/** Format a redeemable-credit cents value as a dollar string (no trailing .00 for whole dollars). */
const formatCredit = (cents: number): string => {
  const dollars = cents / 100
  return dollars % 1 === 0
    ? `$${dollars.toLocaleString()}`
    : `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/**
 * The primary points surface on the points dashboard (ROUTES.POINTS). The reward mechanic is
 * MONTHLY: you must clear your tier's activity goal each month to unlock that month's credit, so the
 * hero number is this month's qualifier progress — not the all-time Voucherify balance (which never
 * resets and doesn't tell you whether you've qualified *this* month). The lifetime redeemable balance
 * (points banked + credit ready to redeem) is kept as an honest secondary line. Presentational only:
 * the wallet fetch lives in EaterLeaderboard so this page fires /wallet/balance once, not per-card.
 */
const MonthlyCreditHero: React.FC<MonthlyCreditHeroProps> = ({ wallet, tier, onRedeem }) => {
  const earned = wallet?.monthlyProgressPoints ?? 0
  // Backend value is authoritative (it bakes in the VIP rollover); fall back to the tier config goal.
  const goal = wallet?.monthlyGoalPoints ?? tier.monthlyGoalPoints
  const pct = goal > 0 ? Math.min(1, earned / goal) : 0
  const progressPercentage = Math.round(pct * 100)
  const remaining = Math.max(0, goal - earned)
  const qualified = goal > 0 && earned >= goal

  const banked = wallet?.pointsBalance ?? 0
  const creditCents = wallet?.walletAvailableCents ?? 0
  const hasRollover = tier.rolloverApplies === true && (tier.rolloverPointsCap ?? 0) > 0

  // Status copy for the three monthly states: not started / in progress / qualified.
  const statusLine = qualified
    ? "You've hit this month's credit 🎉 Keep earning to build next month's."
    : earned === 0
      ? `Start earning to unlock this month's credit — ${goal.toLocaleString()} points gets you there.`
      : `Earn ${remaining.toLocaleString()} more point${remaining === 1 ? '' : 's'} this month to unlock your credit.`

  return (
    <Box
      sx={{
        backgroundColor: 'secondary.50',
        py: { xs: 2.5, md: 4 },
        px: { xs: 2, md: 3 },
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
        <Box display="flex" alignItems="center">
          <Box component="img" src="/images/leaderboard/coin-yellow.png" alt="" sx={{ mr: 0.75 }} />
          <Typography variant="overline" color="grey.600" sx={{ lineHeight: 1.2 }}>
            {tier.name} · This month&apos;s credit
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={onRedeem}
          sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, flexShrink: 0 }}
        >
          View Rewards
        </Button>
      </Box>

      {/* HERO: monthly qualifier progress — the number that actually earns this month's credit. */}
      <Box>
        <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
          <Typography variant="h3" fontWeight={700} sx={{ lineHeight: 1 }}>
            {earned.toLocaleString()}
          </Typography>
          <Typography variant="h6" color="grey.600" fontWeight={500}>
            of {goal.toLocaleString()} points
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          toward this month&apos;s credit
        </Typography>
      </Box>

      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {qualified ? 'Credit unlocked' : `${progressPercentage}% there`}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercentage}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': { borderRadius: 4 },
          }}
        />
      </Box>

      <Typography variant="body2" color="text.secondary">
        {statusLine}
      </Typography>

      {hasRollover && (
        <Typography variant="caption" color="grey.600">
          VIP rollover: up to {(tier.rolloverPointsCap ?? 0).toLocaleString()} qualifying points carry
          into your next cycle.
        </Typography>
      )}

      <Divider sx={{ color: 'grey.300' }} />

      {/* SECONDARY: the lifetime redeemable balance — banked points + credit ready to redeem. */}
      <Box>
        <Typography variant="body2" color="grey.700" fontWeight={600}>
          {banked.toLocaleString()} points banked · {formatCredit(creditCents)} ready to redeem
        </Typography>
        <Typography variant="caption" color="grey.600">
          Your banked points never reset. Redeem your credit whenever you&apos;re ready.
        </Typography>
      </Box>
    </Box>
  )
}

export default MonthlyCreditHero
